<template>
  <li
    :class="{
      'search-autocomplete-section-item': true,
      'search-autocomplete-section-item--selected': currentSelectionId === item.id,
    }"
    @click="handleClick"
  >
    <template v-if="isFFEnabled">
      <AkIcon
        v-if="isRecentSearch"
        size="sm"
        symbol="clock"
        class="search-autocomplete-section-item__type-icon"
      />
      <AkIcon
        v-else-if="isTextSuggestion"
        size="sm"
        symbol="search"
        class="search-autocomplete-section-item__type-icon"
      />
    </template>
    <div
      v-else-if="isRecentSearch"
      class="search-autocomplete-section-item__closer-legacy"
      @click.prevent.stop="removeRecentSearch"
    >
      <AkIcon
        size="sm"
        symbol="x"
      />
    </div>

    <img
      v-if="hasImages"
      class="search-autocomplete-section-item__image search-autocomplete-section-item__image--circle"
      :alt="item.name"
      :src="imgCdnUrl(hasImages.rounded, '36')"
    >
    <img
      v-else-if="hasImageUrl"
      :class="[
        'search-autocomplete-section-item__image',
        isFFEnabled && sectionType === 'boutiques'
          ? 'search-autocomplete-section-item__image--circle'
          : 'search-autocomplete-section-item__image--big',
      ]"
      :alt="item.name"
      :src="hasImageUrl"
    >
    <!-- eslint-disable vue/no-v-html -->
    <span class="ds-flex ds-items-center ds-flex-grow ds-shrink">
      <div class="ds-flex ds-flex-col">
        <span
          :class="{
            'search-autocomplete-section-item__name': true,
            'search-autocomplete-section-item__name--normal': !highlight,
            'search-autocomplete-section-item__name--big': Boolean(hasInfo),
          }"
          v-html="highlightedName"
        />
        <span
          v-if="Boolean(hasInfo)"
          class="search-autocomplete-section-item__info"
        >
          {{ hasInfo }}
        </span>
      </div>
      <span
        v-if="shouldDisplaySubcategory"
        class="search-autocomplete-section-item__subcategory"
      >
        <span class="ds-truncate">{{ itemSubcategoryName }}</span>
      </span>
    </span>

    <div
      v-if="isFFEnabled && isRecentSearch"
      class="search-autocomplete-section-item__closer"
      @click.prevent.stop="removeRecentSearch"
    >
      <AkIcon
        size="sm"
        symbol="x"
      />
    </div>

    <span class="search-autocomplete-section-item__chevron">
      <AkIcon
        size="sm"
        symbol="chevron-right"
        class="search-autocomplete-section-item__icon"
      />
    </span>
  </li>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { imgCdnUrl } from '@bc/shared/util/img-cdn/img-cdn';
import { highlightTextByQuery } from '@core/utilities/string';
import { HANDLE_ITEM_CLICK, HANDLE_ITEM_CLICK_REMOVAL } from '../store/action-types';
import { ItemResult, RESULT_TYPE } from '@bc/discovery/domain/search';
import { SET_ARROW_POSITION } from '../store/mutation-types';
import { getFormattedCategoryName, hasLevelCategory } from '../utils/category';
import { AkIcon } from '@ankorstore/design-system';
import SearchStoreHelpers from '../store/helpers';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';

import { RECENT_SEARCH_GROUP } from '@bc/discovery/feature/search/constants';

const isFFEnabled = isEnabled(FeatureFlag.SearchRecentBrands);

const i18n = useI18n();
const props = withDefaults(
  defineProps<{
    item: ItemResult;
    highlight?: boolean;
    sectionType?: string;
  }>(),
  {
    highlight: false,
    sectionType: undefined,
  }
);

const { originalUserInput, currentSelectionId } = SearchStoreHelpers.useGetters(['originalUserInput', 'currentSelectionId']);
const { [HANDLE_ITEM_CLICK]: handleItemClick, [HANDLE_ITEM_CLICK_REMOVAL]: handleItemClickRemoval } =
  SearchStoreHelpers.useActions([HANDLE_ITEM_CLICK, HANDLE_ITEM_CLICK_REMOVAL]);
const { [SET_ARROW_POSITION]: setArrowPosition } = SearchStoreHelpers.useMutations([SET_ARROW_POSITION]);

const isRecentSearch = computed(() => 'group' in props.item && props.item.group === RECENT_SEARCH_GROUP);

const TEXT_SUGGESTION_TYPES = [RESULT_TYPE.TRENDING, RESULT_TYPE.POPULAR, RESULT_TYPE.CATEGORIES];
const isTextSuggestion = computed(() => 'type' in props.item && TEXT_SUGGESTION_TYPES.includes(props.item.type as RESULT_TYPE));

const highlightedName = computed(() =>
  highlightTextByQuery({
    content: props.item.name,
    query: originalUserInput.value,
    bold: true,
  })
);
const hasImages = computed(() => 'images' in props.item ? props.item.images : undefined);
const hasImageUrl = computed(() => 'imageUrl' in props.item ? props.item.imageUrl : undefined);
const hasInfo = computed(() => 'info' in props.item ? props.item.info : undefined);
const shouldDisplaySubcategory = computed(
  () => Boolean(originalUserInput.value) && hasLevelCategory({ item: props.item, level: 1 })
);
const itemSubcategoryName = computed(() =>
  getFormattedCategoryName({
    item: props.item,
    level: 1,
    i18n,
  })
);

const handleClick = (): void => {
  setArrowPosition(props.item.id);
  handleItemClick({ item: props.item });
};

const removeRecentSearch = (): void => {
  handleItemClickRemoval({ item: props.item });
};
</script>

<style scoped lang="scss">
.search-autocomplete-section-item {
  @apply ds-w-full ds-flex ds-items-center ds-py-2 ds-px-4 ds-cursor-pointer ds-text-left ds-list-none ds-truncate;
  transition: background-color ease-in-out 0.1s;

  &__type-icon {
    @apply ds-text-neutral-500 ds-mr-2 ds-flex-none;
  }

  &__closer-legacy {
    @apply ds-mr-1;
  }

  &__closer {
    @apply ds-ml-2 ds-flex-none ds-text-neutral-500 ds-opacity-0;
    transition: opacity ease-in-out 0.1s;
  }

  &:hover &__closer {
    @apply ds-opacity-100;
  }

  &--selected,
  &:hover {
    @apply ds-bg-neutral-300;
  }

  &__image {
    width: 36px;
    height: 36px;
    @apply ds-rounded-sm ds-border ds-border-solid ds-border-neutral-300 ds-inline ds-flex-initial ds-mr-2 ds-overflow-hidden;
    &--circle {
      @apply ds-rounded-full;
    }
    &--big {
      width: 40px;
      height: 40px;
      @media screen and (min-width: 1024px) {
        width: 48px;
        height: 48px;
      }
    }
  }

  &__name {
    @apply ds-whitespace-pre;

    &--normal {
      @apply ds-font-normal;
    }
    &--big {
      @apply ds-text-sm;
      @media screen and (min-width: 1024px) {
        @apply ds-text-base;
      }
    }
  }

  &__info {
    @apply ds-text-neutral-700 ds-italic;
  }

  &__icon {
    @apply ds-text-neutral-500;
    flex-basis: 40px;
  }

  &__chevron {
    @apply ds-flex-initial md:ds-hidden;
  }

  &__subcategory {
    @apply ds-text-xs ds-text-accent-700 ds-font-semibold ds-pl-1 ds-inline-grid;
    letter-spacing: 0.008em;
  }
}
</style>
